"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueGrowthHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tickerItems = ["DATA-DRIVEN", "OTA OPTIMIZATION", "REVENUE AUDITS", "TECH INTEGRATION", "DIRECT BOOKINGS", "EST. 2024"];
  return <section data-section-id="1752" id="revenuegrowthhero" className="relative w-full overflow-hidden bg-slate-50 text-slate-900">
      {}
      <div className="grid grid-cols-1 md:grid-cols-12 min-h-[88vh] border-b-[4px] border-blue-900">
        {}
        <div ref={leftRef} className="relative md:col-span-6 flex flex-col justify-between px-[clamp(1.25rem,5vw,5rem)] py-12 md:py-16 lg:py-20 border-b-[4px] md:border-b-0 md:border-r-[4px] border-blue-900 overflow-hidden" style={{
        backgroundColor: "hsl(222 47% 98%)"
      }}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          backgroundImage: "radial-gradient(hsl(222 47% 7% / 0.08) 1.2px, transparent 1.4px)",
          backgroundSize: "10px 10px",
          opacity: 0.35
        }} />

          {}
          <div aria-hidden="true" className="pointer-events-none absolute -right-4 top-10 select-none font-default text-blue-900/10" style={{
          fontSize: "clamp(8rem, 20vw, 22rem)",
          lineHeight: 0.8
        }}>
            %
          </div>

          {}
          <div className={`relative z-10 flex items-center gap-3 transition-all duration-500 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <span className="block h-[3px] w-6 bg-blue-900" />
            <span className="font-default uppercase text-blue-900" style={{
            fontSize: "clamp(0.75rem, 0.85vw, 0.875rem)",
            letterSpacing: "0.18em"
          }}>
              § 01 — HotelRevBaBa Consulting
            </span>
          </div>

          {}
          <div className="relative z-10 flex flex-col gap-8 mt-10 md:mt-0">
            <h1 className={`relative font-default text-blue-900 transition-all duration-700 ease-out delay-100 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(3rem, 9vw, 8rem)",
            lineHeight: 0.88,
            letterSpacing: "-0.04em",
            fontWeight: 700
          }}>
              <span className="block">MAXIMIZE</span>
              <span className="block">YOUR</span>
              <span className="relative inline-block">
                {}
                <svg aria-hidden="true" viewBox="0 0 200 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 -z-10 h-full w-full scale-110">
                  <polygon points="100,2 115,18 138,8 138,32 162,28 152,50 178,58 158,70 175,92 148,86 142,108 122,92 100,108 78,92 58,108 52,86 25,92 42,70 22,58 48,50 38,28 62,32 62,8 85,18" fill="hsl(43 46% 55%)" stroke="hsl(222 47% 7%)" strokeWidth="3" strokeLinejoin="round" />
                </svg>
                <span className="relative px-2 text-white">REVENUE.</span>
              </span>
            </h1>

            <p className={`font-default text-slate-800 max-w-[38ch] transition-all duration-700 ease-out delay-200 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(1.05rem, 1.3vw, 1.25rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em"
          }}>
              Expert revenue management strategies for hotel owners. Unlock your property's full potential with data-driven OTA insights and tech-forward solutions.
            </p>
          </div>

          {}
          <div className={`relative z-10 flex flex-wrap items-center gap-4 mt-12 transition-all duration-700 ease-out delay-300 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Link to="/ContactPage">
              <Button className="font-default uppercase bg-blue-900 text-white border-[3px] border-blue-900 rounded-lg h-auto px-8 py-4 text-base hover:bg-blue-800 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-lg transition-all duration-200" style={{
              letterSpacing: "0.04em"
            }}>
                Book a Consultation
                <ArrowRight className="ml-1 h-5 w-5" strokeWidth={2.5} />
              </Button>
            </Link>

            <Link to="#">
              <Button variant="outline" className="font-default uppercase bg-transparent text-blue-900 border-[3px] border-blue-900 rounded-lg h-auto px-8 py-4 text-base hover:bg-blue-900 hover:text-white transition-colors duration-200" style={{
              letterSpacing: "0.04em"
            }}>
                Our Services
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div ref={rightRef} className="relative md:col-span-6 bg-blue-900 overflow-hidden min-h-[60vh] md:min-h-[88vh]">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_analytics.webp" alt="Professional hotel revenue management analytics dashboard" loading="lazy" decoding="async" className={`absolute inset-0 h-full w-full object-cover transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 scale-100" : "opacity-0 scale-105"}`} />

          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          backgroundImage: "radial-gradient(hsl(0 0% 100% / 0.2) 1px, transparent 1.5px)",
          backgroundSize: "8px 8px",
          opacity: 0.3,
          mixBlendMode: "multiply"
        }} />

          {}
          <div className={`absolute top-6 right-6 md:top-10 md:right-10 z-10 transition-all duration-700 ease-out delay-300 ${rightVisible ? "opacity-100 translate-y-0 rotate-[-4deg]" : "opacity-0 -translate-y-4 rotate-0"}`}>
            <div className="bg-amber-500 text-blue-900 border-[3px] border-blue-900 rounded-lg px-4 py-2 font-default uppercase" style={{
            fontSize: "clamp(0.75rem, 0.9vw, 0.9rem)",
            letterSpacing: "0.1em"
          }}>
              Expert Revenue Consulting
            </div>
          </div>

          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-blue-900 hidden md:block" />
        </div>
      </div>

      {}
      <div className="relative w-full bg-white border-b-[4px] border-blue-900 overflow-hidden">
        <div className="flex whitespace-nowrap py-4 animate-[ticker_40s_linear_infinite] hover:[animation-play-state:paused]">
          {[...Array(3)].map((_, dupIdx) => <div key={dupIdx} data-index={dupIdx} className="flex items-center shrink-0">
              {tickerItems.map((item, i) => <div data-index={i} key={`${dupIdx}-${i}`} className="flex items-center shrink-0">
                  <span className="font-default uppercase text-blue-900 px-6" style={{
              fontSize: "0.85rem",
              letterSpacing: "0.12em"
            }}>
                    {item}
                  </span>
                  <span className="font-default text-blue-900" style={{
              fontSize: "0.85rem"
            }}>
                    ••••
                  </span>
                </div>)}
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>;
}